import { useState, useEffect } from 'react'
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase/config'

export const useFeaturedSites = () => {
  const [sites, setSites] = useState({
    data: [],
    loading: true,
    error: null
  })

  useEffect(() => {
    const fetchAllSites = async () => {
      try {
        const sitesQuery = query(
          collection(db, 'sites'),
          where('featured', '==', true)
        );
        
        // Function to shuffle array with daily seed
        const shuffleArray = (array) => {
          const newArray = [...array];
          const today = new Date();
          const seed = today.getFullYear() * 10000 + 
                      (today.getMonth() + 1) * 100 + 
                      today.getDate();
          
          // Simple seeded shuffle algorithm
          for (let i = newArray.length - 1; i > 0; i--) {
            const pseudoRandom = Math.sin(seed + i) * 10000;
            const j = Math.floor((pseudoRandom - Math.floor(pseudoRandom)) * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
          }
          return newArray;
        };
        
        const querySnapshot = await getDocs(sitesQuery);
        const sitesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('All sites query results:', sitesData);
        
        // Ensure each site has the minimum required fields
        const enhancedSitesData = sitesData.map(site => ({
          ...site,
          mainImage: site.mainImage || (site.media?.[0]?.url || null),
          media: site.media || []
        }));
        
        // Now get media data for each site that doesn't have it
        const sitesWithMedia = await Promise.all(
          enhancedSitesData.map(async (site) => {
            try {
              // If media exists and has items, return as is
              if (site.media && site.media.length > 0) {
                return site;
              }
              
              // Fetch site with complete data if media is empty or missing
              const siteRef = doc(db, 'sites', site.id);
              const [siteSnap, mediaSnap] = await Promise.all([
                getDoc(siteRef),
                getDocs(query(
                  collection(db, 'site_media'),
                  where('siteId', '==', site.id)
                ))
              ]);
              
              if (siteSnap.exists()) {
                const fullSiteData = siteSnap.data();
                const mediaData = mediaSnap.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                
              // Filter for valid media URLs
              const validMediaData = mediaData.filter(m => 
                m && m.url && typeof m.url === 'string' && m.url.match(/^https?:\/\/.+/)
              );
              
              // Determine main image
              let mainImage = site.mainImage;
              if (!mainImage && site.primaryMediaId) {
                const primaryMedia = validMediaData.find(m => m.id === site.primaryMediaId);
                if (primaryMedia) {
                  mainImage = primaryMedia.url;
                }
              }
              if (!mainImage && validMediaData.length > 0) {
                mainImage = validMediaData[0].url;
              }
              
              // Final media array with fallback if needed
              const finalMedia = validMediaData.length > 0 ? validMediaData : [{
                id: 'placeholder-' + site.id,
                type: 'image',
                url: '/textures/parchment.png',
                uploadedAt: new Date()
              }];
              
              return {
                ...site,
                ...fullSiteData,
                mainImage: mainImage,
                media: finalMedia
              };
              }
              
              // Fallback to placeholder if fetch fails
              return {
                ...site,
                media: [{
                  id: 'placeholder-' + site.id,
                  type: 'image',
                  url: '/textures/parchment.png',
                  uploadedAt: new Date()
                }]
              };
            } catch (error) {
              console.error(`Error fetching media for site ${site.id}:`, error);
              
              // If anything fails, ensure there's a placeholder
              return {
                ...site,
                media: [{
                  id: 'placeholder-' + site.id,
                  type: 'image',
                  url: '/textures/parchment.png',
                  uploadedAt: new Date()
                }]
              };
            }
          })
        );
        
        console.log('All sites with media:', sitesWithMedia);
        setSites({
          data: sitesWithMedia,
          loading: false,
          error: null
        });
      } catch (error) {
        console.error('Failed to load sites:', error);
        setSites(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load sites'
        }));
      }
    };

    fetchAllSites();
  }, []);

  return sites
}
